import { Button, Input, PlusIcon, Separator } from '@chain-zen/ui'
import { useEffect, useState } from 'react'
import type { IWorkspace } from 'src/types/workspace'

import type { UserDetails } from '../../../../../types/user'
import { MemberListItem } from './member-list-item'

interface MemberSelectionListProps {
  isLoadingMember: boolean
  members: { user: UserDetails }[]
  membersWorkspace: IWorkspace[]
  onInviteClick: (value: boolean) => void
  onMemberRemove: (user: UserDetails) => void
  onMemberSelect: (user: UserDetails) => void
  onSearchInput: (input: string) => void
  selectedMembers: UserDetails[]
}

/**
 *This component is used to display the list of members in the workspace.
 It lets the user select members from the workspace to add to the shipment.
 */
export const MemberSelectionList = ({
  members,
  selectedMembers,
  onMemberSelect,
  onMemberRemove,
  onInviteClick,
  isLoadingMember,
  onSearchInput,
  membersWorkspace
}: MemberSelectionListProps) => {
  const sortedMembers = [...members].sort((a, b) => a.user.name?.localeCompare(b.user.name || '') || 0)
  const [searchInput, setSearchInput] = useState('')

  const [loadingUsers, setLoadingUsers] = useState<Record<string, boolean>>({})

  const handleMemberSelect = (user: UserDetails) => {
    setLoadingUsers((prev) => ({ ...prev, [user.id]: true })) // Set loading for clicked user
    onMemberSelect(user)
  }

  const handleMemberRemove = (user: UserDetails) => {
    setLoadingUsers((prev) => ({ ...prev, [user.id]: true })) // Set loading for clicked user
    onMemberRemove(user)
  }

  // Clear loading state when isLoadingMember changes to false
  useEffect(() => {
    if (!isLoadingMember) {
      setLoadingUsers({})
    }
  }, [isLoadingMember])

  return (
    <div className="w-full max-w-full overflow-hidden">
      <Input
        name="emailOrPhone"
        placeholder="Email or phone"
        onChange={(e) => {
          onSearchInput(e.target.value)
          setSearchInput(e.target.value)
        }}
        className="mb-2 focus-visible:ring-0"
      />

      <div className="max-h-40 z-10 overflow-auto pr-2" role="listbox">
        {sortedMembers?.map(({ user }) => {
          if (!user) return null

          return (
            <MemberListItem
              key={user.id}
              user={user}
              isSelected={selectedMembers.some((selected) => selected.id === user.id)}
              isLoading={!!loadingUsers[user.id] && isLoadingMember}
              onSelect={handleMemberSelect}
              onRemove={handleMemberRemove}
              memberWorkspace={membersWorkspace?.find((workspace) => workspace?.id === user?.workspaceID)}
            />
          )
        })}
      </div>

      {members.length > 0 && <Separator className="my-1" />}

      <Button
        variant="ghost"
        className="w-full justify-start px-4 text-xs font-normal text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 h-auto"
        onClick={() => onInviteClick(true)}
      >
        <PlusIcon className="h-4 w-4 mr-2" />
        {searchInput ? `Add ${searchInput}` : 'Invite user'}
      </Button>
    </div>
  )
}
